import { AxiosError } from 'axios'
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router'

import { AccountDetails } from './AccountDetails'
import { ClinTheme } from '../../../ClinTheme'
import { AnnounceMode } from '../../../components/ClinAnnounceBar/ClinAnnounceBar'
import { ICrumb } from '../../../components/ClinBreadcrumb'
import { ClinSpinner } from '../../../components/ClinSpinner'
import { StyledSpinnerContainer } from '../../../components/ClinSpinner/ClinSpinner.styles'
import {
  ContactStatus,
  isAusGaUser,
  LovName,
  UserRole,
  UserRoleRecord
} from '../../../constants'
import { useAppContext } from '../../../context/app'
import {
  clearAnnounceEvent,
  createAnnounceEvent
} from '../../../events/AnnounceEvent'
import { createGetUserAuthEvent } from '../../../events/AuthEvent'
import { createShowInstituteModalEvent } from '../../../events/InstituteModalEvent'
import { useCheckUnableHomePage } from '../../../hooks/useCheckUnableHomePage/useCheckUnableHomePage'
import { AnalyticsEvent } from '../../../services/Analytics'
import analyticsServiceSingleton from '../../../services/Analytics/initAnalytics'
import {
  AuthError,
  activateUser,
  cancelActivateUser,
  cancelContactsSearch,
  contactsSearch,
  deleteContactFromInstitution,
  deleteShippingAddress,
  newShippingAddress,
  queryUserLoginStatus,
  updateContact,
  updatePassword
} from '../../../services/ApiService'
import {
  ContactResponseDto,
  ContactSearchDto,
  CreateShippingAddressDto,
  InstituteContactDto,
  OrgAddressDto,
  UpdateContactDto,
  UserDto,
  UserEmailDto,
  UserStatusDto
} from '../../../types/swaggerTypes'
import { useLov } from '../../../utils/useLov'
import useChangeBackgroundColor from '../../../hooks/useChangeBackgroundColor/useChangeBackgroundColor'
import authService from '../../../services/AuthService'
import { noop } from '../../../utils/noop'
import { IContact } from '../../../types/IContact'
import { augmentContactWithStatus } from '../../InstitutePages/InstituteDetail/InstituteDetail.model'
import { IPagination } from '../../../components/ClinPagination/ClinPagination.model'
import { SortDirectionType } from '../../../components/ClinTableOrderToggle/ClinTableOrderToggle'
import { RemoveAddressConfirmation } from '../../../features/RemoveAddressConfirmation'
import { RemoveUserConfirmation } from '../../../features/RemoveUserConfirmation'
import { useCountries } from '../../../utils/useCountries'
import { FormSubmitState } from './AccountDetailsChangePassword'

export enum EditableFields {
  firstName = 'First Name',
  lastName = 'Last Name',
  preferredName = 'Preferred Name',
  password = 'Password',
  classification = 'Job Type and License number',
  specialism = 'Specialism',
  licenseNumber = 'License Number',
  phoneNumber = 'Phone Number',
  mobileNumber = 'Mobile Number',
  faxNumber = 'Fax Number'
}

/**
 * Convert enum to translation key
 * @param value
 */
export const getLocalisationKeyForValue = (value: EditableFields): string => {
  switch (value) {
    case EditableFields.classification:
      return 'common:labels.classification'
    case EditableFields.firstName:
      return 'common:labels.classification'
    case EditableFields.lastName:
      return 'common:labels.full_name'
    case EditableFields.licenseNumber:
      return 'common:labels.full_name'
    case EditableFields.password:
      return 'common:labels.full_name'
    case EditableFields.phoneNumber:
      return 'common:labels.phone_number_one'
    case EditableFields.preferredName:
      return 'common:labels.preferred_name'
    case EditableFields.specialism:
      return 'common:labels.specialism'
    default:
      return 'common:missing'
  }
}

export enum MarketingMode {
  Editable = 'Editable',
  Submitting = 'Submitting',
  Submitted = 'Submitted',
  Error = 'Error'
}

const defaultSearchParams: ContactSearchDto = {
  query: '*',
  filter: {
    contactName: '',
    role: ''
  },
  pagination: {
    skip: 0,
    take: 1000
  },
  sorting: {
    sortBy: '',
    order: SortDirectionType.Descending
  }
}

const rowsPerPage: number = 2

const defaultPagination: IPagination = {
  count: 0,
  skip: 0,
  take: rowsPerPage,
  total: 0
}

export enum ModalMode {
  confirmUserDeletion,
  confirmAddressDeletion
}

interface ModalState {
  mode: ModalMode | undefined
  isLoading: boolean
  isInvite: boolean
  isSubmitting: boolean
  isSubmitted: boolean
  confirmAction?: boolean
  userToDelete?: ContactResponseDto
  addressToDelete?: OrgAddressDto
}

const defaultState: ModalState = {
  mode: undefined,
  isLoading: false,
  isInvite: false,
  isSubmitting: false,
  isSubmitted: false
}

interface SuccessAnnounce {
  title: string | undefined
  message: string | undefined
}

export const AccountDetailsContainer: FunctionComponent = () => {
  const {
    dispatch,
    institute,
    userDetails,
    portfolioCountryCode,
    userRole,
    supportContact
  } = useAppContext()
  const history = useHistory()
  const [currentDetails, setCurrentDetails] = useState<UserDto | undefined>(
    userDetails
  )
  const [activeField, setActiveField] = useState<
    EditableFields | string | undefined
  >()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [marketingMode, setMarketingMode] = useState<MarketingMode>(
    MarketingMode.Editable
  )
  const { isLovLoading: isSpecialismsLoading, lovValues: specialisms } = useLov(
    LovName.SPECIALISM
  )
  const { isLovLoading: isJobTitlesLoading, lovValues: jobTitles } = useLov(
    LovName.JOB_TITLES
  )
  const [contacts, setContacts] = useState<IContact[]>([])
  const [activeContacts, setActiveContacts] = useState<IContact[]>([])
  const [pendingContacts, setPendingContacts] = useState<IContact[]>([])
  const [searchParams, setSearchParams] =
    useState<ContactSearchDto>(defaultSearchParams)
  const [pagination, setPagination] = useState<IPagination>(defaultPagination)
  const [modalState, setModalState] = useState<ModalState>(defaultState)
  const location = useLocation().state as SuccessAnnounce
  const isAdmin = !!(userRole && UserRoleRecord[userRole as UserRole].isAdmin)
  const [isLoadingContacts, setIsLoadingContacts] = useState<boolean>(true)
  const [isSubmittingAddress, setIsSubmittingAddress] = useState<boolean>(false)
  const [addressModalError, setAddressModalError] = useState<string>('')
  const [resetForm, setResetForm] = useState<boolean>(false)
  const { countries } = useCountries()
  const [passwordFormState, setPasswordFormState] = useState<FormSubmitState>(
    FormSubmitState.Form
  )
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [isUserSubmitting, setIsUserSubmitting] = useState(false)
  const [isRequestNewAddress, setIsRequestNewAddress] = useState(false)

  useChangeBackgroundColor(ClinTheme.colors.lightGrey)

  // Set up current language for switcher
  const { t } = useTranslation()
  const { enableNewHomePage } = useCheckUnableHomePage()

  const crumbs: ICrumb[] = [
    isAusGaUser(portfolioCountryCode, userRole) && enableNewHomePage
      ? { path: '/home', name: t('navigation:home') }
      : { path: '/orders', name: t('orders:page_name') },
    { name: t('account_details:title'), path: '/account' }
  ]

  useEffect(() => {
    setCurrentDetails(userDetails)
  }, [userDetails])

  const handleFormSubmission = (data: UpdateContactDto) => {
    setIsSubmitting(true)
    userDetails &&
      updateContact(userDetails.contactCard.contactId.toString(), data)
        .then((response) => {
          if (response.status === 200) {
            dispatch(createGetUserAuthEvent())
            dispatch(
              createAnnounceEvent(
                AnnounceMode.Success,
                t('account_details:account_details_updated'),
                ' '
              )
            )
            setActiveField(undefined)
            setIsSubmitting(false)
          }
        })
        .catch((error: AxiosError) => {
          const { code, message } = error
          // If request is cancelled continue
          if (error.message === AuthError.RequestCancelled) {
            return
          }
          // If we have a full error show it
          if (error.response) {
            const { title, detail } = error.response.data
            dispatch(
              createAnnounceEvent(
                AnnounceMode.Error,
                `${t('account_details:error_updating_details')} ${
                  title ? title : ''
                } ${message ? message : ''}`
              )
            )
            console.warn(title, detail)
          } else {
            dispatch(
              createAnnounceEvent(
                AnnounceMode.Error,
                `${t('account_details:error_updating_details')} ${message} ${
                  code ? code : ''
                }`
              )
            )
          }
          setIsSubmitting(false)
          window.scrollTo(0, 0)
        })
  }

  const handleChangeInstitute = () => {
    if (marketingMode === MarketingMode.Submitting) {
      return
    }
    dispatch(createShowInstituteModalEvent())
  }

  const handleEditRequest = (field: EditableFields | string) => {
    dispatch(clearAnnounceEvent())
    if (marketingMode === MarketingMode.Submitting) {
      return
    }

    window.scrollTo(0, 0)
    setActiveField(field)
    setEditingKey(null)
    setIsRequestNewAddress(false)

    if (field === EditableFields.password) {
      analyticsServiceSingleton.trackEvent(AnalyticsEvent.PasswordChange)
    }
  }

  const handleRequestToGoBack = () => {
    dispatch(clearAnnounceEvent())
    const redirectUrl =
      isAusGaUser(portfolioCountryCode, userRole) && enableNewHomePage
        ? '/home'
        : '/orders'
    history.push(redirectUrl)
  }

  const handleRequestToExitEditing = () => {
    setMarketingMode(MarketingMode.Editable)
    setActiveField(undefined)
  }

  const handleLogOut = () => {
    authService.logout().then(() => noop())
  }

  const handlePageClicked = (selectedPageIndex: number) => {
    setIsLoadingContacts(true)
    setSearchParams({
      ...searchParams,
      pagination: {
        skip: (selectedPageIndex - 1) * rowsPerPage,
        take: rowsPerPage
      }
    })
  }
  const handlePageSizeChange = (pageSize: number) => {
    cancelContactsSearch()
    setIsLoadingContacts(true)
    setSearchParams({
      ...searchParams,
      pagination: {
        skip: 0,
        take: pageSize
      }
    })
  }

  useEffect(() => {
    location &&
      location.message !== null &&
      location.title !== null &&
      dispatch(
        createAnnounceEvent(
          AnnounceMode.Success,
          location.message,
          location.title
        )
      )
  }, [dispatch, location])

  // Helper function to remove duplicates from contact arrays
  const removeDuplicateContacts = useCallback(
    (contacts: IContact[]): IContact[] => {
      const uniqueContacts = new Map<string, IContact>()

      contacts.forEach((contact) => {
        // Use contactId as primary key, fallback to contactEmail if contactId is not available
        const uniqueKey = contact.contactId
          ? contact.contactId.toString()
          : contact.contactEmail

        // Only add if we haven't seen this contact before, or if this contact has more recent data
        if (!uniqueContacts.has(uniqueKey)) {
          uniqueContacts.set(uniqueKey, contact)
        }
      })

      return Array.from(uniqueContacts.values())
    },
    []
  )

  const getContacts = useCallback(() => {
    contactsSearch(searchParams)
      .then((response) => {
        if (response) {
          const contactsFromAPI: IContact[] = response.data.result.map((c) => {
            return {
              ...c,
              status: ContactStatus.INACTIVE
            }
          })
          // Remove duplicates from the initial API response
          const uniqueContacts = removeDuplicateContacts(contactsFromAPI)
          setContacts(uniqueContacts)
          const paginationDto = response.data.pagination
          setPagination({
            count: paginationDto.count,
            skip: paginationDto.skip,
            take: paginationDto.take,
            total: paginationDto.total
          })
        }
      })
      .catch((error: AxiosError) => {
        const { code, message } = error

        // If request is cancelled continue
        if (error.message === AuthError.RequestCancelled) {
          return
        }
        // If we have a full error show it
        if (error.response) {
          const { title, detail } = error.response.data
          dispatch(
            createAnnounceEvent(
              AnnounceMode.Error,
              `There was an error fetching contacts. ${title ?? ''} ${
                message ?? ''
              }`
            )
          )
          console.warn(title, detail)
        } else {
          dispatch(
            createAnnounceEvent(
              AnnounceMode.Error,
              `There was an error fetching your contacts. ${message} ${
                code ?? ''
              }`
            )
          )
        }

        analyticsServiceSingleton.trackError(
          error,
          JSON.parse(localStorage.getItem('current_user') || '{}')
        )
        setIsLoadingContacts(false)
      })
    return cancelContactsSearch()
  }, [dispatch, searchParams])

  useEffect(() => {
    if (isAdmin) {
      getContacts()
    } else {
      setIsLoadingContacts(false)
    }
  }, [searchParams, isAdmin, dispatch, getContacts])

  useEffect(() => {
    if (contacts && contacts.length > 0) {
      const contactEmails: UserEmailDto[] = contacts.map((contact) => {
        return {
          email: contact.contactEmail
        }
      })

      setIsLoadingContacts(true)
      contactEmails &&
        contactEmails.length > 0 &&
        queryUserLoginStatus(contactEmails)
          .then((response) => {
            const statuses: UserStatusDto[] = response.data
            const pendingContacts: IContact[] = removeDuplicateContacts(
              augmentContactWithStatus(
                statuses,
                contacts,
                ContactStatus.PENDING
              )
            )
            const activeContacts: IContact[] = removeDuplicateContacts(
              augmentContactWithStatus(statuses, contacts, ContactStatus.ACTIVE)
            )
            const errorContacts: IContact[] = removeDuplicateContacts(
              augmentContactWithStatus(statuses, contacts, ContactStatus.ERROR)
            )
            const inactiveContacts: IContact[] = removeDuplicateContacts(
              augmentContactWithStatus(
                statuses,
                contacts,
                ContactStatus.INACTIVE
              )
            )
            const otherContacts: IContact[] = [
              ...errorContacts,
              ...inactiveContacts
            ]
            if (otherContacts.length > 0) {
              otherContacts.forEach((contact) =>
                console.warn(`${contact.contactEmail} is ${contact.status}`)
              )
            }
            setPendingContacts(pendingContacts)
            setActiveContacts(activeContacts)
          })
          .catch((error: AxiosError) => {
            dispatch(
              createAnnounceEvent(
                AnnounceMode.Error,
                `There was an error fetching your contacts. ${error.message}`
              )
            )
          })
          .finally(() => setIsLoadingContacts(false))
    }
  }, [contacts, dispatch, getContacts])

  /**
   * Methods related to contact change requests
   */
  const handleRequestNewUser = () => {
    history.push('/new-user')
  }

  const handleRemoveUser = (user: ContactResponseDto, isInvite: boolean) => {
    setModalState({
      ...modalState,
      mode: ModalMode.confirmUserDeletion,
      userToDelete: user,
      isInvite
    })
  }

  const handleSubmitRemoveUser = () => {
    setModalState({
      ...modalState,
      isSubmitting: true
    })
    modalState.mode === ModalMode.confirmUserDeletion &&
      modalState.userToDelete &&
      deleteContactFromInstitution(modalState.userToDelete.contactId.toString())
        .then((response) => {
          if (response.status === 200) {
            analyticsServiceSingleton.trackEvent(
              AnalyticsEvent.RemoveUserFromInstitute
            )
            setModalState({
              ...modalState,
              mode: undefined,
              isSubmitting: false,
              isSubmitted: true,
              userToDelete: undefined
            })
            window.scrollTo(0, 0)
            dispatch(
              createAnnounceEvent(
                AnnounceMode.Success,
                'The contact was successfully removed',
                ' '
              )
            )
            getContacts()
          }
        })
        .catch((error) => {
          const { code, message } = error
          // If request is cancelled continue
          if (error.message === AuthError.RequestCancelled) {
            return
          }
          window.scrollTo(0, 0)
          setModalState({
            ...modalState,
            mode: undefined,
            isSubmitting: false,
            isSubmitted: false,
            userToDelete: undefined
          })
          // If we have a full error show it
          if (error.response) {
            const { title, detail } = error.response.data
            dispatch(
              createAnnounceEvent(
                AnnounceMode.Error,
                `There was an error removing this user. ${title ?? ''} ${
                  message ?? ''
                }`
              )
            )
            console.warn(title, detail)
          } else {
            dispatch(
              createAnnounceEvent(
                AnnounceMode.Error,
                `There was an error removing this user. ${message} ${
                  code ?? ''
                }`
              )
            )
          }
        })
  }

  const handleEditUser = (userKey: string) => {
    setEditingKey(userKey)
    setActiveField(undefined)
    setIsRequestNewAddress(false)
  }

  const handleSubmitRemoveAddress = () => {
    setModalState({
      ...modalState,
      isSubmitting: true
    })
    modalState.mode === ModalMode.confirmAddressDeletion &&
      modalState.addressToDelete &&
      deleteShippingAddress(modalState.addressToDelete.addressId)
        .then((response) => {
          if (response.data.incidentNumber)
            setModalState({
              ...modalState,
              mode: undefined,
              isSubmitting: false,
              isSubmitted: true,
              addressToDelete: undefined
            })
          window.scrollTo(0, 0)
          dispatch(
            createAnnounceEvent(
              AnnounceMode.Success,
              'Your request to remove a shipping request has been submitted',
              ' '
            )
          )
        })
        .catch((error) => {
          const { code, message } = error
          // If request is cancelled continue
          if (error.message === AuthError.RequestCancelled) {
            return
          }
          window.scrollTo(0, 0)
          setModalState({
            ...modalState,
            mode: undefined,
            isSubmitting: false,
            isSubmitted: false,
            addressToDelete: undefined
          })
          // If we have a full error show it
          if (error.response) {
            const { title, detail } = error.response.data
            dispatch(
              createAnnounceEvent(
                AnnounceMode.Error,
                `There was an error submitting your request. ${title ?? ''} ${
                  message ?? ''
                }`
              )
            )
            console.warn(title, detail)
          } else {
            dispatch(
              createAnnounceEvent(
                AnnounceMode.Error,
                `There was an error submitting your request. ${message} ${
                  code ?? ''
                }`
              )
            )
          }
        })
  }

  const handleCancelModal = () => {
    const updatedModalState = {
      ...modalState,
      mode: undefined,
      userToDelete: undefined,
      addressToDelete: undefined
    }
    setModalState(updatedModalState)
  }

  const handleRemoveAddress = (address: OrgAddressDto) => {
    setModalState({
      ...modalState,
      mode: ModalMode.confirmAddressDeletion,
      addressToDelete: address
    })
  }

  const handleResendInvite = (contact: IContact) => {
    const updatedPendingContacts = pendingContacts.map((pendingContact) => {
      if (pendingContact.contactEmail === contact.contactEmail) {
        pendingContact.isLoading = true
      }
      return pendingContact
    })
    setPendingContacts([...updatedPendingContacts])

    activateUser({ email: contact.contactEmail })
      .then((response) => {
        if (response.status === 200) {
          window.scrollTo(0, 0)
          dispatch(
            createAnnounceEvent(
              AnnounceMode.Success,
              'You can view the status of their invitation below.',
              'Your invitation has been sent'
            )
          )
          const updatedPendingContacts = pendingContacts.map(
            (pendingContact) => {
              if (pendingContact.contactEmail === contact.contactEmail) {
                pendingContact.isLoading = false
              }
              return pendingContact
            }
          )
          setPendingContacts([...updatedPendingContacts])
        }
      })
      .catch((error: AxiosError) => {
        dispatch(createAnnounceEvent(AnnounceMode.Error, `${error.message}`))
      })
      .finally(() => {
        window.scrollTo(0, 0)
      })
  }

  useEffect(() => {
    return () => {
      cancelContactsSearch()
      cancelActivateUser()
    }
  }, [])

  const handleNewShippingAddress = (data: CreateShippingAddressDto) => {
    setIsSubmittingAddress(true)
    newShippingAddress(data)
      .then((response) => {
        if (response?.data.incidentNumber) {
          analyticsServiceSingleton.trackEvent(
            AnalyticsEvent.ShippingAddressSubmit,
            { incidentNumber: response?.data.incidentNumber }
          )
          dispatch(
            createAnnounceEvent(
              AnnounceMode.Success,
              t('request_new_shipping_address:submitted_ok_message'),
              t('request_new_shipping_address:submitted_ok_title')
            )
          )
          setIsRequestNewAddress(false)
          setAddressModalError('')
          setResetForm(true)

          const addressScroll = document.getElementById('scrollView')
          addressScroll?.scrollTo({
            behavior: 'smooth',
            top: 0
          })
        }
      })
      .catch((error: AxiosError) => {
        const { code, message } = error
        setResetForm(false)
        // If request is cancelled continue
        if (error.message === AuthError.RequestCancelled) {
          return
        }
        // If we have a full error show it
        if (error.response && error.response.data) {
          const { title, detail } = error.response.data
          dispatch(
            createAnnounceEvent(
              AnnounceMode.Error,
              `${t('request_new_shipping_address:submitted_fail_message')} ${
                title ? title : ''
              } ${message ? message : ''}`
            )
          )
          console.warn(title, detail)
        } else {
          dispatch(
            createAnnounceEvent(
              AnnounceMode.Error,
              `${t(
                'request_new_shipping_address:submitted_fail_message'
              )} ${message} ${code ? code : ''}`
            )
          )
        }
      })
      .finally(() => {
        setIsSubmittingAddress(false)
        window.scrollTo(0, 0)
      })
  }

  const handleChangePasswordSubmit = (
    password: string,
    newPassword: string,
    newPasswordConfirmation: string
  ) => {
    setPasswordFormState(FormSubmitState.Loading)

    updatePassword(password, newPassword, newPasswordConfirmation)
      .then(() => {
        dispatch(
          createAnnounceEvent(
            AnnounceMode.Success,
            `Your password has been updated`
          )
        )
        setPasswordFormState(FormSubmitState.Success)
        setActiveField(undefined)
      })
      .catch((error: AxiosError) =>
        setTimeout(() => {
          setPasswordFormState(FormSubmitState.Error)
          const { code, message } = error
          dispatch(
            createAnnounceEvent(
              AnnounceMode.Error,
              `Error posting new password. ${message} [${code ? code : '?'}]`
            )
          )
          analyticsServiceSingleton.trackError(
            error,
            JSON.parse(localStorage.getItem('current_user') || '{}')
          )
        }, 500)
      )
  }

  const handleUserFormSubmission = (
    data: UpdateContactDto,
    contact: InstituteContactDto
  ) => {
    setIsUserSubmitting(true)
    contact?.contactId &&
      updateContact(contact?.contactId.toString(), data)
        .then((response) => {
          if (response.status === 200) {
            setIsUserSubmitting(false)
            setEditingKey(null)

            window.scrollTo(0, 0)
            dispatch(
              createAnnounceEvent(
                AnnounceMode.Success,
                'The contact was successfully updated',
                ' '
              )
            )

            setIsLoadingContacts(true)
            getContacts()
          }
        })
        .catch((error: AxiosError) => {
          const { code, message } = error

          // If request is cancelled continue
          if (error.message === AuthError.RequestCancelled) {
            return
          }
          // If we have a full error show it
          if (error.response) {
            const { title, detail } = error.response.data
            dispatch(
              createAnnounceEvent(
                AnnounceMode.Error,
                `${t('edit_contact:error_submitting_updates')} ${
                  title ? title : ''
                } ${message ? message : ''}`
              )
            )
            console.warn(title, detail)
          } else {
            dispatch(
              createAnnounceEvent(
                AnnounceMode.Error,
                `${t('edit_contact:error_submitting_updates')} ${message} ${
                  code ? code : ''
                }`
              )
            )
          }
          setIsUserSubmitting(false)
          window.scrollTo(0, 0)
        })
  }

  const handleUserFormCancel = () => {
    setEditingKey(null)
  }

  const handleRequestNewShippingAddress = () => {
    setEditingKey(null)
    setActiveField(undefined)
    setIsRequestNewAddress(true)
  }

  return (
    <>
      <RemoveAddressConfirmation
        isLoading={modalState.isLoading}
        isOpen={modalState.mode === ModalMode.confirmAddressDeletion}
        isSubmitting={modalState.isSubmitting}
        handleClose={handleCancelModal}
        handleSubmit={handleSubmitRemoveAddress}
      />
      <RemoveUserConfirmation
        contact={modalState.userToDelete}
        isLoading={modalState.isLoading}
        isOpen={modalState.mode === ModalMode.confirmUserDeletion}
        isInvite={modalState.isInvite}
        isSubmitting={modalState.isSubmitting}
        handleClose={handleCancelModal}
        handleSubmit={handleSubmitRemoveUser}
      />
      {isSpecialismsLoading ||
      isJobTitlesLoading ||
      !institute.data ||
      !currentDetails ? (
        <StyledSpinnerContainer>
          <ClinSpinner size={ClinTheme.space[7]} />
        </StyledSpinnerContainer>
      ) : (
        <AccountDetails
          isSubmitting={isSubmitting}
          fieldToEdit={activeField}
          institute={institute.data}
          userDetails={currentDetails.contactCard}
          pagination={pagination}
          activeContacts={activeContacts}
          pendingContacts={pendingContacts}
          specialisms={specialisms}
          jobTitles={jobTitles}
          isSpecialismsLoading={isSpecialismsLoading}
          isJobTitlesLoading={isJobTitlesLoading}
          crumbs={crumbs}
          backButtonText={
            isAusGaUser(portfolioCountryCode, userRole)
              ? t('common:buttons.back_to_home')
              : t('common:buttons.back')
          }
          passwordFormState={passwordFormState}
          handleFormSubmission={handleFormSubmission}
          handleChangeInstitute={handleChangeInstitute}
          handleEditRequest={handleEditRequest}
          handleRequestToExitEditing={handleRequestToExitEditing}
          handleRequestToGoBack={handleRequestToGoBack}
          handleLogOut={handleLogOut}
          handleChangePasswordSubmit={handleChangePasswordSubmit}
          onRequestNewUser={handleRequestNewUser}
          onEditUser={handleEditUser}
          onRemoveUser={handleRemoveUser}
          onRequestInvite={handleResendInvite}
          onPageClicked={handlePageClicked}
          onPageSizeChange={handlePageSizeChange}
          onRequestNewShippingAddress={handleRequestNewShippingAddress}
          onRemoveAddress={handleRemoveAddress}
          isAdmin={isAdmin}
          supportContact={supportContact}
          addressesShipTo={institute.data?.shipTo || []}
          isLoadingContacts={isLoadingContacts}
          editingKey={editingKey}
          handleUserFormSubmission={handleUserFormSubmission}
          handleUserFormCancel={handleUserFormCancel}
          isUserSubmitting={isUserSubmitting}
          handleAddressSubmission={handleNewShippingAddress}
          isSubmittingAddress={isSubmittingAddress}
          resetOnSubmit={resetForm}
          countries={countries}
          selectedCountry={portfolioCountryCode}
          addressErrorMessage={addressModalError}
          isRequestNewAddress={isRequestNewAddress}
          setIsRequestNewAddress={setIsRequestNewAddress}
        />
      )}
    </>
  )
}
